// src/view/Project/DataRoom/DataRoom.tsx
//
// Client Data Room: a file browser for the gated project (Tata Motors).
// Two branches in one tree:
//   - deployment (read-only): project -> site -> location -> deployment ->
//     audios/images. Files listed on demand; view/download only.
//   - custom (writable): project- or site-anchored folders, arbitrarily
//     nested, with upload/create-folder/delete for anyone with project access.

import { useCallback, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Button,
  Breadcrumbs,
  Link as MuiLink,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  LinearProgress,
  Chip,
} from '@mui/material';
import {
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  Audiotrack as AudioIcon,
  CreateNewFolder as NewFolderIcon,
  Upload as UploadIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  MoreVert as MoreIcon,
  ArrowBack as BackIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';

import { useSnackbar } from 'src/view/components/SnackbarService';
import { SnackbarType } from 'src/view/components/SnackbarService';
import {
  dataRoomApi,
  uploadToSession,
  TreeNode,
  DeploymentFile,
} from 'src/api/dataRoom';
import FilePreview from './FilePreview';

// A location within the browser. For the custom branch we track the current
// folder node id; for deployment we track synthetic ids from the tree.
interface Crumb {
  id: string | null; // null = root
  name: string;
}

const formatBytes = (n?: number | null): string => {
  if (n == null) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

const fileIconFor = (contentType?: string | null) => {
  if (contentType?.startsWith('image/')) return <ImageIcon color="action" />;
  if (contentType?.startsWith('audio/')) return <AudioIcon color="action" />;
  return <FileIcon color="action" />;
};

const DataRoom = () => {
  const { id: projectId } = useParams<{ id: string }>();
  const { showSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [nodes, setNodes] = useState<TreeNode[]>([]);

  // Navigation: a stack of crumbs. The last crumb is the current folder.
  // We keep separate handling for the two branches via the current node's data.
  const [crumbs, setCrumbs] = useState<Crumb[]>([{ id: null, name: 'Home' }]);

  // For deployment audios/images folders we fetch real files lazily.
  const [deploymentFiles, setDeploymentFiles] = useState<DeploymentFile[] | null>(null);

  // Dialogs / menus
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuNode, setMenuNode] = useState<TreeNode | null>(null);

  // Upload progress { fileName: pct }
  const [uploads, setUploads] = useState<Record<string, number>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Preview
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewNode, setPreviewNode] = useState<{ name: string; contentType?: string | null } | null>(null);

  const loadTree = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const res = await dataRoomApi.getTree(projectId);
      setNodes(res.nodes);
    } catch (e: any) {
      showSnackbar({ message: e?.message || 'Failed to load data room', type: SnackbarType.ERROR });
    } finally {
      setLoading(false);
    }
  }, [projectId, showSnackbar]);

  useEffect(() => {
    loadTree();
  }, [loadTree]);

  const currentCrumb = crumbs[crumbs.length - 1];
  const currentId = currentCrumb.id;

  // Determine what "current folder" means and resolve children.
  // Synthetic deployment ids look like: 'deployment-root', 'dep:site:<id>',
  // 'dep:loc:<id>', 'dep:dep:<id>', 'dep:sub:<depId>:audios|images'.
  const currentNode = useMemo(
    () => nodes.find((n) => n.id === currentId) || null,
    [nodes, currentId],
  );

  // Is the current folder inside the read-only deployment branch?
  const inDeploymentBranch =
    currentId === 'deployment-root' ||
    (currentId?.startsWith('dep:') ?? false) ||
    currentNode?.branch === 'deployment';

  // For the writable branch, the current scope/site/parent used when creating
  // folders or uploading here.
  const writeContext = useMemo(() => {
    if (inDeploymentBranch) return null;
    if (!currentNode) {
      // At Home root -> project scope, no parent.
      return { scope: 'project' as const, site_id: undefined, parent_id: undefined };
    }
    return {
      scope: (currentNode.scope || 'project') as 'project' | 'site',
      site_id: currentNode.site_id || undefined,
      parent_id: currentNode.node_type === 'folder' ? currentNode.id : currentNode.parent_id || undefined,
    };
  }, [currentNode, inDeploymentBranch]);

  // Children shown at the current level.
  const children = useMemo<TreeNode[]>(() => {
    // Deployment audios/images leaf -> handled via deploymentFiles fetch.
    if (currentId?.startsWith('dep:sub:')) return [];

    if (currentId === null) {
      // Root: show the "Deployment Data" node + top-level custom nodes.
      const dep = nodes.filter((n) => n.id === 'deployment-root');
      const custom = nodes.filter(
        (n) => n.branch === 'custom' && n.scope === 'project' && !n.parent_id,
      );
      return [...dep, ...custom];
    }

    // Deployment branch: children are nodes whose dep_parent === currentId.
    if (currentId === 'deployment-root' || currentId.startsWith('dep:')) {
      return nodes.filter((n) => n.branch === 'deployment' && n.dep_parent === currentId);
    }

    // Custom branch folder: children are custom nodes whose parent_id === currentId.
    return nodes.filter((n) => n.branch === 'custom' && n.parent_id === currentId);
  }, [nodes, currentId]);

  // When entering a deployment audios/images folder, fetch real files.
  useEffect(() => {
    const fetchDeploymentFiles = async () => {
      if (!projectId || !currentId?.startsWith('dep:sub:')) {
        setDeploymentFiles(null);
        return;
      }
      const rest = currentId.slice('dep:sub:'.length); // <depId>:<folder>
      const sep = rest.lastIndexOf(':');
      const deploymentId = rest.slice(0, sep);
      const folder = rest.slice(sep + 1) as 'audios' | 'images';
      setDeploymentFiles(null);
      try {
        const res = await dataRoomApi.listDeploymentFiles(projectId, deploymentId, folder);
        setDeploymentFiles(res.files);
      } catch (e: any) {
        showSnackbar({ message: e?.message || 'Failed to list files', type: SnackbarType.ERROR });
        setDeploymentFiles([]);
      }
    };
    fetchDeploymentFiles();
  }, [projectId, currentId, showSnackbar]);

  const enterFolder = (node: TreeNode) => {
    setCrumbs((c) => [...c, { id: node.id, name: node.name }]);
  };
  const goToCrumb = (index: number) => {
    setCrumbs((c) => c.slice(0, index + 1));
  };

  const openFile = async (opts: { gcs_path: string; name: string; content_type?: string | null }) => {
    if (!projectId) return;
    try {
      const { download_url } = await dataRoomApi.getDownloadUrl(projectId, opts.gcs_path);
      setPreviewUrl(download_url);
      setPreviewNode({ name: opts.name, contentType: opts.content_type });
    } catch (e: any) {
      showSnackbar({ message: e?.message || 'Could not open file', type: SnackbarType.ERROR });
    }
  };

  const handleCreateFolder = async () => {
    if (!projectId || !writeContext) return;
    const name = newFolderName.trim();
    if (!name) return;
    try {
      await dataRoomApi.createFolder(projectId, {
        name,
        scope: writeContext.scope,
        site_id: writeContext.site_id,
        parent_id: writeContext.parent_id,
      });
      showSnackbar({ message: 'Folder created', type: SnackbarType.SUCCESS });
      setNewFolderOpen(false);
      setNewFolderName('');
      await loadTree();
    } catch (e: any) {
      showSnackbar({ message: e?.message || 'Failed to create folder', type: SnackbarType.ERROR });
    }
  };

  const handleFilesChosen = async (files: FileList | null) => {
    if (!projectId || !writeContext || !files || files.length === 0) return;
    for (const file of Array.from(files)) {
      const key = file.name;
      setUploads((u) => ({ ...u, [key]: 0 }));
      try {
        const { upload_session_uri, gcs_path } = await dataRoomApi.initiateUpload(projectId, {
          file_name: file.name,
          content_type: file.type || 'application/octet-stream',
          scope: writeContext.scope,
          site_id: writeContext.site_id,
          parent_id: writeContext.parent_id,
        });
        await uploadToSession(upload_session_uri, file, (pct) =>
          setUploads((u) => ({ ...u, [key]: pct })),
        );
        await dataRoomApi.finalizeUpload(projectId, {
          file_name: file.name,
          gcs_path,
          content_type: file.type || undefined,
          scope: writeContext.scope,
          site_id: writeContext.site_id,
          parent_id: writeContext.parent_id,
        });
        showSnackbar({ message: `Uploaded ${file.name}`, type: SnackbarType.SUCCESS });
      } catch (e: any) {
        showSnackbar({ message: e?.message || `Failed to upload ${file.name}`, type: SnackbarType.ERROR });
      } finally {
        setUploads((u) => {
          const next = { ...u };
          delete next[key];
          return next;
        });
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
    await loadTree();
  };

  const handleDelete = async (node: TreeNode) => {
    if (!projectId) return;
    setMenuAnchor(null);
    if (!window.confirm(`Delete "${node.name}"${node.node_type === 'folder' ? ' and everything inside it' : ''}?`)) {
      return;
    }
    try {
      await dataRoomApi.deleteNode(projectId, node.id);
      showSnackbar({ message: 'Deleted', type: SnackbarType.SUCCESS });
      await loadTree();
    } catch (e: any) {
      showSnackbar({ message: e?.message || 'Failed to delete', type: SnackbarType.ERROR });
    }
  };

  const canWriteHere = !inDeploymentBranch && !!writeContext;
  const activeUploads = Object.entries(uploads);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>
          Data Room
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<NewFolderIcon />}
            disabled={!canWriteHere}
            onClick={() => setNewFolderOpen(true)}
          >
            New Folder
          </Button>
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            disabled={!canWriteHere}
            onClick={() => fileInputRef.current?.click()}
          >
            Upload
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            onChange={(e) => handleFilesChosen(e.target.files)}
          />
        </Box>
      </Box>

      {/* Breadcrumbs */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        {crumbs.length > 1 && (
          <IconButton size="small" onClick={() => goToCrumb(crumbs.length - 2)} sx={{ mr: 1 }}>
            <BackIcon fontSize="small" />
          </IconButton>
        )}
        <Breadcrumbs>
          {crumbs.map((c, i) => (
            <MuiLink
              key={`${c.id}-${i}`}
              component="button"
              underline="hover"
              color={i === crumbs.length - 1 ? 'text.primary' : 'inherit'}
              onClick={() => goToCrumb(i)}
            >
              {c.name}
            </MuiLink>
          ))}
        </Breadcrumbs>
      </Box>

      {inDeploymentBranch && (
        <Chip
          icon={<LockIcon />}
          label="Read-only — deployment data is managed by Darukaa"
          size="small"
          sx={{ mb: 2 }}
        />
      )}

      {/* Active uploads */}
      {activeUploads.length > 0 && (
        <Box sx={{ mb: 2 }}>
          {activeUploads.map(([name, pct]) => (
            <Box key={name} sx={{ mb: 1 }}>
              <Typography variant="caption">{name} — {pct}%</Typography>
              <LinearProgress variant="determinate" value={pct} />
            </Box>
          ))}
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          {/* Deployment audios/images leaf: show real files */}
          {currentId?.startsWith('dep:sub:') ? (
            deploymentFiles === null ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : deploymentFiles.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 4 }}>
                No files here yet.
              </Typography>
            ) : (
              deploymentFiles.map((f) => (
                <Row
                  key={f.gcs_path}
                  icon={fileIconFor(f.content_type)}
                  name={f.name}
                  meta={formatBytes(f.size_bytes)}
                  onOpen={() => openFile({ gcs_path: f.gcs_path, name: f.name, content_type: f.content_type })}
                />
              ))
            )
          ) : children.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4 }}>
              {inDeploymentBranch ? 'Nothing here.' : 'This folder is empty. Create a folder or upload files.'}
            </Typography>
          ) : (
            children.map((node) => (
              <Row
                key={node.id}
                icon={
                  node.node_type === 'folder' ? (
                    <FolderIcon sx={{ color: '#f5b942' }} />
                  ) : (
                    fileIconFor(node.content_type)
                  )
                }
                name={node.name}
                meta={node.node_type === 'file' ? formatBytes(node.size_bytes) : undefined}
                lock={node.branch === 'deployment'}
                onOpen={() => {
                  if (node.node_type === 'folder') enterFolder(node);
                  else if (node.gcs_path)
                    openFile({ gcs_path: node.gcs_path, name: node.name, content_type: node.content_type });
                }}
                onMenu={
                  node.branch === 'custom'
                    ? (el) => {
                        setMenuAnchor(el);
                        setMenuNode(node);
                      }
                    : undefined
                }
              />
            ))
          )}
        </Box>
      )}

      {/* Row context menu (custom branch only) */}
      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
        <MenuItem
          onClick={() => menuNode && handleDelete(menuNode)}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* New folder dialog */}
      <Dialog open={newFolderOpen} onClose={() => setNewFolderOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>New folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Folder name"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewFolderOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateFolder}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview */}
      <FilePreview
        open={!!previewUrl}
        url={previewUrl}
        name={previewNode?.name || ''}
        contentType={previewNode?.contentType}
        onClose={() => {
          setPreviewUrl(null);
          setPreviewNode(null);
        }}
      />
    </Box>
  );
};

// A single browser row.
const Row = ({
  icon,
  name,
  meta,
  lock,
  onOpen,
  onMenu,
}: {
  icon: ReactNode;
  name: string;
  meta?: string;
  lock?: boolean;
  onOpen: () => void;
  onMenu?: (el: HTMLElement) => void;
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      px: 1.5,
      py: 1.25,
      borderRadius: 1,
      cursor: 'pointer',
      '&:hover': { bgcolor: 'action.hover' },
    }}
    onClick={onOpen}
  >
    <Box sx={{ mr: 1.5, display: 'flex' }}>{icon}</Box>
    <Typography sx={{ flex: 1 }} noWrap>
      {name}
    </Typography>
    {lock && (
      <Tooltip title="Read-only">
        <LockIcon fontSize="small" sx={{ color: 'text.disabled', mr: 1 }} />
      </Tooltip>
    )}
    {meta && (
      <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
        {meta}
      </Typography>
    )}
    {onMenu ? (
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onMenu(e.currentTarget);
        }}
      >
        <MoreIcon fontSize="small" />
      </IconButton>
    ) : (
      <DownloadIcon fontSize="small" sx={{ color: 'text.disabled', visibility: 'hidden' }} />
    )}
  </Box>
);

export default DataRoom;
